/**
 * FFmpeg error diagnostics — payload enrichment for wasm_process events.
 *
 * Problem: when FFmpeg WASM throws during exec, the user-facing toast is
 * derived from `err.message` (200 chars truncated). For env-specific bugs
 * (e.g. raw AAC ES + adeclick filter graph init failure on Chrome 8 GB)
 * that's not enough to reproduce locally — we'd need:
 *
 *   • the actual FFmpeg log tail (filter init lines have the real cause,
 *     `err.message` only gets the final exit-code summary)
 *   • the file's container/codec fingerprint (first 64 bytes — codec
 *     metadata, no PII)
 *   • the browser's AAC variant support (LC vs HE vs HE-v2) when input
 *     is AAC, since native decode availability changes the recovery path
 *
 * Enrichment is applied at the `__aeToolError` call site in ffmpeg-
 * processor.js via this helper. Centralised so future enrichment
 * (per-codec probes, capability snapshots) lands in one place instead
 * of N error sites.
 *
 * Cost: ~500-2500 bytes per error event. Errors are rare (~50/day across
 * all FFmpeg-using tools), so total volume bump is < 0.5 % of analytics
 * (~80 KB / day vs ~50-100 MB / day baseline).
 *
 * Privacy: `header_hex` is the first 64 bytes of the file = container
 * magic + codec parameters (sync word, profile, sample rate index,
 * channel config). No audio content, no metadata strings, no PII.
 */
(function() {
'use strict';

// Cache the AAC capability probe so repeated errors in the same session
// don't re-probe MediaSource. Probe is cheap (~ms) but pointless to repeat.
var _aacCaps = null;
function probeAacCaps() {
    if (_aacCaps !== null) return _aacCaps;
    if (typeof MediaSource === 'undefined' || typeof MediaSource.isTypeSupported !== 'function') {
        _aacCaps = '';
        return _aacCaps;
    }
    // Encode as 3-char binary string: "lc he hev2" e.g. "110" = LC + HE
    // supported, HE-v2 not. mp4a.40.X codes:
    //   .2  = AAC LC      (universal since Safari 4 / Chrome 4)
    //   .5  = AAC HE      (HE-AAC v1, SBR)
    //   .29 = AAC HE-v2   (HE-AAC v2, SBR + PS)
    // Tested via 'audio/mp4' container — the standard probe target.
    try {
        var lc = MediaSource.isTypeSupported('audio/mp4;codecs="mp4a.40.2"') ? '1' : '0';
        var he = MediaSource.isTypeSupported('audio/mp4;codecs="mp4a.40.5"') ? '1' : '0';
        var hev2 = MediaSource.isTypeSupported('audio/mp4;codecs="mp4a.40.29"') ? '1' : '0';
        _aacCaps = lc + he + hev2;
    } catch (_) {
        _aacCaps = '';
    }
    return _aacCaps;
}

function bytesToHex(u8, n) {
    if (!u8 || !u8.length) return '';
    var len = Math.min(n, u8.length);
    var out = new Array(len);
    for (var i = 0; i < len; i++) {
        var b = u8[i];
        out[i] = (b < 16 ? '0' : '') + b.toString(16);
    }
    return out.join('');
}

function isAacFile(file) {
    if (!file) return false;
    var name = (file.name || '').toLowerCase();
    var type = (file.type || '').toLowerCase();
    return name.endsWith('.aac') || type === 'audio/aac' || name.endsWith('.m4a') || type === 'audio/mp4';
}

/**
 * Enrich a wasm_process error payload with diagnostic fields.
 *
 * @param {Object} payload  Original payload built at the error site.
 * @param {Object} ctx      { file: File|Blob, bytes: Uint8Array, logs: string[] }
 * @returns {Object}        Mutated payload with extra fields.
 */
window.__enrichFFmpegError = function(payload, ctx) {
    if (!payload) payload = {};
    if (!ctx) return payload;

    // Field 1: ff_log_tail — last salient FFmpeg lines. __ffErrTail (defined
    // alongside __ffErrText in ffmpeg-processor.js init) already filters
    // noise and returns the most informative tail. Capped at 1500 chars to
    // bound payload size on long compile-time chatter.
    try {
        if (typeof window.__ffErrTail === 'function' && Array.isArray(ctx.logs) && ctx.logs.length) {
            var tail = window.__ffErrTail(ctx.logs);
            if (tail) payload.ff_log_tail = String(tail).substring(0, 1500);
        }
    } catch (_) {}

    // Field 2: header_hex — first 64 bytes of input as lowercase hex.
    // Codec metadata only; no audio content. For AAC ADTS this gives sync
    // word + profile + sample rate + channel config — enough to synthesize
    // a repro fixture from the prod telemetry record.
    try {
        if (ctx.bytes && ctx.bytes.length > 0) {
            payload.header_hex = bytesToHex(ctx.bytes, 64);
        }
    } catch (_) {}

    // Field 3: aac_caps — only for AAC-family files. 3-char binary
    // string {LC, HE, HE-v2}. Tells us whether the user's browser can
    // natively decode the variant they uploaded — distinguishes
    // "filter graph init bug" from "browser can't decode this codec".
    try {
        if (isAacFile(ctx.file)) {
            var caps = probeAacCaps();
            if (caps) payload.aac_caps = caps;
        }
    } catch (_) {}

    return payload;
};

})();
